import React, { useState } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { Users, X, Loader, CheckCircle, AlertTriangle, Send, Eye, MessageSquare } from 'lucide-react';

const ChaiRmansCouncilModal = ({ isOpen, onClose, initialQuery = '', esgContext = null }) => {
  const [query, setQuery] = useState(initialQuery);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [currentStage, setCurrentStage] = useState('input');
  const [showStage, setShowStage] = useState('all'); // 'all', 'stage1', 'stage2', 'stage3'

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!query.trim()) {
      setError('Please enter a question');
      return;
    }

    setLoading(true);
    setError('');
    setCurrentStage('stage1');

    const data = await apiFetch(`${API}/advisory/chairmans-council`, {
      method: 'POST',
      body: {
        query,
        context: esgContext
      }
    });

    if (data?._error) {
      setError(data.message || 'Failed to get council advisory');
      setLoading(false);
      setCurrentStage('input');
      return;
    }

    setResult(data);
    setCurrentStage('complete');
    setLoading(false);
  };

  const renderStageIndicator = (stage, label, isActive, isComplete) => (
    <div className={`flex items-center gap-2 ${isActive ? 'text-blue-600' : isComplete ? 'text-green-600' : 'text-slate-400'}`}>
      {isComplete ? (
        <CheckCircle size={20} />
      ) : isActive ? (
        <Loader className="animate-spin" size={20} />
      ) : (
        <div className="w-5 h-5 rounded-full border-2 border-current" />
      )}
      <span className="text-sm font-medium">{label}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-700 p-6 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users size={32} />
            <div>
              <h2 className="text-2xl font-bold">Chairman's Council</h2>
              <p className="text-purple-100 text-sm">Multi-LLM Consensus Advisory</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Council Members */}
        <div className="p-4 bg-purple-50 border-b border-purple-200">
          <div className="flex items-center gap-4 text-xs">
            <span className="font-semibold text-purple-900">Council Members:</span>
            <div className="flex gap-2">
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">GPT-5 (General Reasoning)</span>
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded">Claude Sonnet 4 (Ethics)</span>
              <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">Gemini 2.5 Flash (Analysis)</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Input Stage */}
          {!result && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Your ESG Question
                </label>
                <textarea
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Ask your ESG question here... (e.g., 'How should we approach Scope 3 GHG emissions reporting for our supply chain?')"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  rows={4}
                  disabled={loading}
                />
              </div>

              {esgContext && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs font-semibold text-blue-900 mb-2">Context Provided:</p>
                  <div className="text-xs text-blue-700 space-y-1">
                    {esgContext.compliance_score && <p>• ESRS Compliance: {esgContext.compliance_score}%</p>}
                    {esgContext.double_material_topics && <p>• Double Material Topics: {esgContext.double_material_topics}</p>}
                    {esgContext.financial_exposure && <p>• Financial Exposure: ${esgContext.financial_exposure.toLocaleString()}</p>}
                  </div>
                </div>
              )}

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={loading || !query.trim()}
                className="w-full py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    Consulting Council...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Consult Council
                  </>
                )}
              </button>
            </div>
          )}

          {/* Loading Stages */}
          {loading && (
            <div className="space-y-6 mt-6">
              <div className="flex items-center justify-between">
                {renderStageIndicator('stage1', '1. First Opinions', currentStage === 'stage1', currentStage === 'stage2' || currentStage === 'complete')}
                <div className="flex-1 h-0.5 bg-slate-200 mx-4" />
                {renderStageIndicator('stage2', '2. Cross-Reviews', currentStage === 'stage2', currentStage === 'complete')}
                <div className="flex-1 h-0.5 bg-slate-200 mx-4" />
                {renderStageIndicator('stage3', '3. Synthesis', currentStage === 'complete', false)}
              </div>

              <div className="text-center text-slate-600">
                <p className="text-sm">
                  {currentStage === 'stage1' && 'Gathering opinions from all council members...'}
                  {currentStage === 'stage2' && 'Models reviewing each other\'s responses...'}
                  {currentStage === 'complete' && 'Synthesizing consensus-based recommendation...'}
                </p>
              </div>
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="space-y-6">
              {/* Stage Filter Tabs */}
              <div className="flex gap-2 border-b border-slate-200 pb-2">
                <button
                  onClick={() => setShowStage('all')}
                  className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
                    showStage === 'all' ? 'bg-purple-100 text-purple-700' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  Final Synthesis
                </button>
                <button
                  onClick={() => setShowStage('stage1')}
                  className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
                    showStage === 'stage1' ? 'bg-purple-100 text-purple-700' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Eye size={16} className="inline mr-1" />
                  Stage 1: Opinions
                </button>
                <button
                  onClick={() => setShowStage('stage2')}
                  className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
                    showStage === 'stage2' ? 'bg-purple-100 text-purple-700' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <MessageSquare size={16} className="inline mr-1" />
                  Stage 2: Reviews
                </button>
              </div>

              {/* Final Synthesis */}
              {(showStage === 'all') && result.stage_3_synthesis && (
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-slate-900">Chairman's Consensus</h3>
                  
                  <div className="prose max-w-none">
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-l-4 border-purple-500 p-4 rounded-lg">
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">{result.stage_3_synthesis.full_text}</p>
                    </div>
                  </div>

                  {result.stage_3_synthesis.consensus_points.length > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                        <CheckCircle size={18} />
                        Consensus Points
                      </h4>
                      <ul className="space-y-1">
                        {result.stage_3_synthesis.consensus_points.map((point, idx) => (
                          <li key={idx} className="text-sm text-green-800">• {point}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.stage_3_synthesis.recommendations.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">Recommendations</h4>
                      <ol className="space-y-1 list-decimal list-inside">
                        {result.stage_3_synthesis.recommendations.map((rec, idx) => (
                          <li key={idx} className="text-sm text-blue-800">{rec}</li>
                        ))}
                      </ol>
                    </div>
                  )}

                  <div className="text-xs text-slate-500 border-t pt-3">
                    Based on analysis from {result.stage_3_synthesis.metadata?.models_consulted.join(', ')}
                  </div>
                </div>
              )}

              {/* Stage 1: Opinions */}
              {showStage === 'stage1' && result.stage_1_opinions && (
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-slate-900">Individual Opinions</h3>
                  {result.stage_1_opinions.map((opinion, idx) => (
                    <div key={idx} className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="bg-slate-100 px-4 py-2 flex items-center justify-between">
                        <div>
                          <span className="font-semibold text-slate-900">{opinion.model}</span>
                          <span className="text-xs text-slate-600 ml-2">({opinion.role})</span>
                        </div>
                      </div>
                      <div className="p-4 bg-white">
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{opinion.opinion}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Stage 2: Reviews */}
              {showStage === 'stage2' && result.stage_2_reviews && (
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-slate-900">Cross-Reviews</h3>
                  {result.stage_2_reviews.map((review, idx) => (
                    <div key={idx} className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="bg-slate-100 px-4 py-2">
                        <span className="font-semibold text-slate-900">{review.reviewer}'s Review</span>
                      </div>
                      <div className="p-4 bg-white">
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{review.review}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* New Query Button */}
              <button
                onClick={() => {
                  setResult(null);
                  setQuery('');
                  setCurrentStage('input');
                }}
                className="w-full py-2 bg-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-300 transition-colors"
              >
                Ask Another Question
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChaiRmansCouncilModal;
